/**
 * Settings Screen - Authentication Actions
 *
 * Extracted from app/(tabs)/settings.tsx (Line Count Refactor 2025-11-21)
 *
 * Handles user sign-out with platform-specific confirmation dialogs.
 * Web skips confirmation (Platform.OS check), native shows confirmation alert.
 *
 * Phase: 1B - Production Code Refactoring
 * Refactored: 2025-11-21 (settings.tsx: 345 → 172 lines)
 */

import { Platform } from 'react-native';
import type { Href } from 'expo-router';
import type { TFunction } from 'i18next';
import { captureEvent } from '../analytics';

export interface AuthActionsParams {
  alert: (title: string, message: string, buttons?: any[]) => void;
  t: TFunction<'settings'>;
  tCommon: TFunction<'common'>;
  signOut: () => Promise<{ error?: Error | null }>;
  router: {
    replace: (href: Href) => void;
  };
}

/**
 * Handles user sign-out with platform-specific confirmation.
 *
 * Platform Differences:
 * - Web: No confirmation alert, direct sign-out with window.location redirect
 * - Native: Confirmation alert before sign-out
 *
 * Workflow:
 * 1. Platform.OS === 'web': Sign out immediately, redirect to /login
 * 2. Native: Show confirmation alert
 * 3. On confirm: Sign out, navigate to /login
 * 4. Track analytics event
 *
 * @param params Auth action parameters
 */
export async function handleSignOut(params: AuthActionsParams): Promise<void> {
  const { alert, t, tCommon, signOut, router } = params;

  // Web doesn't support Alert.alert, so we skip confirmation on web
  if (Platform.OS === 'web') {
    console.log('[Settings] Sign out requested (web - no confirmation)');

    // Track sign out action
    captureEvent('feature_used', {
      feature: 'sign_out',
      screen: 'settings',
      user_tenure_days: 0,
    });

    const { error } = await signOut();
    if (error) {
      console.error('[Settings] Sign out error:', error);
      alert(tCommon('error'), error.message);
      return;
    }

    console.log('[Settings] Sign out successful, navigating to login');

    // On web, use window.location.href for reliable navigation
    window.location.href = '/login';
    return;
  }

  // Native platforms show confirmation dialog
  alert(
    t('account_actions.sign_out_confirm_title'),
    t('account_actions.sign_out_confirm_message'),
    [
      { text: tCommon('cancel'), style: 'cancel' },
      {
        text: t('account_actions.sign_out'),
        style: 'destructive',
        onPress: async () => {
          console.log('[Settings] Sign out requested');

          // Track sign out action
          captureEvent('feature_used', {
            feature: 'sign_out',
            screen: 'settings',
            user_tenure_days: 0,
          });

          const { error } = await signOut();
          if (error) {
            console.error('[Settings] Sign out error:', error);
            alert(tCommon('error'), error.message);
            return;
          }

          console.log('[Settings] Sign out successful, navigating to login');
          router.replace('/login');
        },
      },
    ]
  );
}
